/** Small shared building blocks of the Agent-Studio (styles: agentStudio.css). */
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightArrowLeft, faBookOpen, faCalculator, faChevronRight, faDatabase, faEnvelope, faGlobe, faInbox,
  faMagnifyingGlass, faPaperPlane, faPlug, faShare, faUserTie,
} from '@fortawesome/free-solid-svg-icons';
import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';

// MUI fields and buttons as in the platform's modals (purple focus, grey label)
export const MUI_FIELD = {
  '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#5e1bff', borderWidth: '1px' },
  '& .MuiInputLabel-root': { color: 'gray', '&.Mui-focused': { color: '#5e1bff' } },
};
export const MUI_CONTAINED = { backgroundColor: '#5616ea', '&:hover': { backgroundColor: '#4a14d6' } };
export const MUI_OUTLINED = { color: '#5616ea', borderColor: '#5616ea', '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' } };

export function Spinner({ size = 14 }) {
  return <CircularProgress size={size} thickness={5} className="as-spinner" />;
}

export function Toggle({ checked, onChange, disabled, title }) {
  return (
    <Switch
      size="small"
      checked={!!checked}
      disabled={disabled}
      title={title}
      inputProps={title ? { 'aria-label': title } : undefined}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => onChange(e.target.checked)}
      className="as-toggle"
    />
  );
}

export function Badge({ children, tone = 'grey', title }) {
  return <span className={`as-badge as-badge-${tone}`} title={title}>{children}</span>;
}

export function Markdown({ text }) {
  return (
    <div className="as-md">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{ a: (p) => <a {...p} target="_blank" rel="noopener noreferrer">{p.children}</a> }}
      >
        {text || ''}
      </ReactMarkdown>
    </div>
  );
}

export function Collapsible({ title, children, defaultOpen = false, right, className = '' }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`as-collapsible ${className}`}>
      <div className="as-collapsible-head">
        <button type="button" onClick={() => setOpen((o) => !o)} className="as-collapsible-toggle" aria-expanded={open}>
          <FontAwesomeIcon icon={faChevronRight} className={`as-chevron ${open ? 'is-open' : ''}`} />
          {title}
        </button>
        {right}
      </div>
      {open && <div className="as-collapsible-body">{children}</div>}
    </div>
  );
}

export function JsonView({ value }) {
  let text;
  try {
    text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  } catch (_) {
    text = String(value);
  }
  return <pre className="as-json">{text}</pre>;
}

const EFFECT_ICONS = {
  compute: faCalculator,
  save: faDatabase,
  send: faPaperPlane,
  forward: faShare,
  handoff: faUserTie,
  knowledge: faBookOpen,
  route: faArrowRightArrowLeft,
  lookup: faMagnifyingGlass,
};

export function EffectIcon({ effect, className = '' }) {
  return <FontAwesomeIcon icon={EFFECT_ICONS[effect] || faMagnifyingGlass} className={className} />;
}

/** How a tool connects, in plain words: { icon, label, detail }. */
export function toolConnection(tool, integrations, t) {
  const cfg = tool.config || {};
  const integ = (integrations || []).find((i) => i.id === cfg.integration_id);
  switch (tool.kind) {
    case 'code':
      return { icon: faCalculator, label: t('agent_studio_tool_code'), detail: tool.name };
    case 'save_record':
      return { icon: faDatabase, label: t('agent_studio_tool_saves_to'), detail: cfg.collection };
    case 'find_records':
      return { icon: faDatabase, label: t('agent_studio_tool_reads_from'), detail: cfg.collection };
    case 'update_record':
      return { icon: faDatabase, label: t('agent_studio_tool_updates_in'), detail: cfg.collection };
    case 'notify_owner':
      return { icon: faInbox, label: t('agent_studio_tool_forwards'), detail: cfg.category || '' };
    case 'send_email':
      return { icon: faEnvelope, label: t('agent_studio_tool_emails'), detail: emailRecipients(cfg, t).join(', ') };
    case 'skill_action': { // a step taken over from the old platform: an API call shows its host
      const step = cfg.action || {};
      let host = '';
      try { host = step.type === 'http' ? new URL(step.http?.url).host : ''; } catch (_) { /* templated or empty URL */ }
      return { icon: host ? faGlobe : faPlug, label: host ? 'API' : t('agent_studio_tool_classic'), detail: host || tool.name };
    }
    case 'mcp':
      return { icon: faPlug, label: 'MCP', detail: `${integ?.name || ''} · ${cfg.tool_name || ''}` };
    case 'app_action': {
      // catalog tools are named "<App>: <action>"
      const [app, ...rest] = (tool.name || '').split(': ');
      return { icon: faPlug, label: rest.length ? app : cfg.provider, detail: rest.length ? rest.join(': ') : tool.name };
    }
    case 'http': {
      let host = integ?.name || 'API';
      try { host = new URL(cfg.url).host; } catch (_) { /* templated URL */ }
      return { icon: faGlobe, label: 'API', detail: host };
    }
    default:
      return { icon: faPlug, label: tool.kind, detail: tool.name };
  }
}

/** An e-mail tool's recipients in plain words: fixed addresses as they are, a placeholder as "the customer's address". */
export function emailRecipients(cfg, t) {
  const to = Array.isArray(cfg?.to) ? cfg.to : String(cfg?.to || '').split(/[,;]/);
  const out = to.map((x) => String(x).trim()).filter(Boolean)
    .map((x) => (/\{/.test(x) ? t('agent_studio_email_customer') : x));
  return [...new Set(out)];
}

/** An e-mail tool as the recipient sees it: to, subject and the HTML in a sandboxed frame (no scripts). */
export function EmailPreview({ config, t }) {
  return (
    <div className="as-email">
      <dl className="as-email-head">
        <dt>{t('agent_studio_email_to')}</dt><dd>{emailRecipients(config, t).join(', ')}</dd>
        <dt>{t('agent_studio_email_subject')}</dt><dd>{config?.subject || ''}</dd>
      </dl>
      <iframe className="as-email-body" title={config?.subject || 'e-mail'} sandbox="" srcDoc={config?.html || ''} />
    </div>
  );
}

export function Empty({ children }) {
  return <div className="as-empty">{children}</div>;
}

/**
 * The header of a right-hand panel (Build: skills/behavior/knowledge/profile, Test:
 * what happened/data): one segmented tab bar, identical in both modes. tabs:
 * [{ key, label, count?, dot? }]; `before` is shown left of the bar (mobile back link).
 */
// With icons (tab.icon), the inactive tabs show only their icon and count, so five tabs fit the panel.
// `lead`: a control inside the tab bar, left of the first tab (the panel's hide button)
export function PanelTabs({ tabs, active, onPick, before = null, lead = null }) {
  const icons = tabs.some((tab) => tab.icon);
  return (
    <div className="as-panel-head">
      {before}
      <div className={`as-panel-tabs${icons ? ' has-icons' : ''}`} role="tablist">
        {lead}
        {tabs.map((tab) => (
          <button key={tab.key} type="button" role="tab" aria-selected={active === tab.key}
            aria-label={icons ? `${tab.label}${tab.count != null ? ` ${tab.count}` : ''}` : undefined}
            className={active === tab.key ? 'is-active' : ''} onClick={() => onPick(tab.key)}>
            {icons ? (
              // every tab named: the icon (with its count as a badge) above the name
              <>
                <span className="as-tab-glyph">
                  {tab.icon && <FontAwesomeIcon icon={tab.icon} className="as-tab-icon" />}
                  {tab.count != null && <span className="as-tab-count">{tab.count}</span>}
                </span>
                <span className="as-tab-label">{tab.label}</span>
              </>
            ) : (
              <>
                {tab.label}
                {tab.count != null && <span className="as-tab-count">{tab.count}</span>}
              </>
            )}
            {tab.dot && active !== tab.key && <span className="as-tab-dot" />}
          </button>
        ))}
      </div>
    </div>
  );
}
